<template>
  <div class="timeline sm:flex items-center items-stretch justify-between relative">
    <div class="line hidden md:block lg:block" />
    <div
      v-for="(event, index) in events"
      :key="index"
      class="event flex-1 sm:flex-wrap"
    >
      <div class="flex flex-col flex-1 items-center relative">
        <div 
          ref="circleRefs" 
          class="circle text-center inline-flex items-center justify-center"
          :class="{selected: event.selected, current: event.current}" 
          @mouseover="showPopover(event, index)" 
          @mouseleave="hidePopover(event)"
        >
          <i
            v-if="event.current"
            class="fas fa-star"
          />
          <i
            v-if="event.done"
            class="fas fa-check"
          />
        </div>
        <div class="font-semibold text-center">
          {{ event.name }}
        </div>
      </div>
      <div 
        v-show="event.selected" 
        ref="popoverRefs" 
        role="tooltip" 
        style="z-index: 9999;"
        class="tooltip isolate w-72 text-base leading-snug text-lightBlue-900 bg-lightBlue-200 rounded-lg shadow-xl"
      >
        <div class="px-3 py-2 bg-lightBlue-800 rounded-t-lg">
          <h3 class="font-semibold text-white">
            {{ event.name }}
          </h3>
        </div>
        <div
          class="px-3 py-2"
          v-html="event.description"
        />
        <div
          v-if="event.done"
          class="px-3 py-2 bg-lightBlue-800 rounded-b-lg"
        >
          <h3 class="font-semibold text-white">
            Done: {{ event.done }}
          </h3>
        </div>
        <div
          id="arrow"
          data-popper-arrow="true"
          class="arrow"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { createPopper } from "@popperjs/core";

export default {
  data() {
    return {
      events: [
        { 
          name: 'Request Submitted', 
          description: `
            The community sends its request, with a petition signed by 10 residences, to the 
            District Supervisor, who forwards it to FCDOT.
          `, 
          selected: false,
          done: '06/02/2025',
        },
        {
          name: 'Qualifying', 
          description: `
            FCDOT performs a study to determine if the road meets the qualification criteria.
            If it qualifies, the community forms a task force to work with FCDOT on plan development.
          `, 
          selected: false,
          done: '08/29/2025',
        },
        { 
          name: 'Develop the Plan', 
          description: `
            The task force works with FCDOT and VDOT to develop a traffic calming plan. Once there is 
            draft plan, FCDOT determines the ballot area based on the plan.
          `, 
          selected: false,
          current: false,
          done: '10/23/2025',
        },        
        { 
          name: 'Affected Property Owners', 
          description: `
            The task force negotiates with, and hopefully obtains approval from, the property owners
            directly adjacent to proposed traffic calming devices.  If an owner will not approve a
            device, it is moved if possible.  If FCDOT does not allow it to be moved, the device is 
            removed from the plan.  If FCDOT determines the plan is now unsafe due to removed devices,
            the plan cannot move forward.
          `, 
          selected: false,
          done: '11/13/2025',
        },
        { 
          name: 'Community Outreach',
          description: `
            If enough affected property owners have approved traffic devices, and FCDOT says the plan is still 
            safe and can move forward, the plan will now be stable enough to advertise to the community. The task 
            force is now able to work on building consensus and support in the ballot area.
          `, 
          selected: false,
          current: false,
          done: '04/26/2026',
        },
        /*
          ONCE THE MEETING IS SCHEDULED...
          <p class="mt-0 mb-2">
            FCDOT has scheduled a virtual community meeting to present the Fairway Drive Traffic Calming Proposal 
            on <span class="font-semibold">Wednesday, September 27, 2023 at 6:30 PM</span>.  The meeting will be 
            conducted using Microsoft Teams and the meeting will be captioned.  As required by FCDOT, on 8/27 we 
            hand delivered an official flyer for the meeting to all 109 residences in the ballot area.  The flyer 
            has the meeting details, and information needed to connect to the meeting.  We have also emailed out
            the details to our maling list.
          </p>
          <p class="mt-0">
            After the meeting, the task force determines whether to move forward to the community vote (based on 
            whether there seems to be enough community support in the ballot area for the plan to pass) or ask 
            FCDOT to revise the plan based on community feedback.
          </p>
        */
        { 
          name: 'Official Public Meeting', 
          description: `
            <p class="mt-0 mb-2">
              The task force and FCDOT hold a community informational meeting. Notification is provided to 
              all residents in the ballot area. After the meeting, the task force determines whether to move forward to the 
              community vote (based on whether there seems to be enough community support in the ballot area 
              for the plan to pass) or ask FCDOT to revise the plan based on community feedback.
            </p>
          `, 
          selected: false,
          current: false,
          done: '02/19/2026',
        },
        { 
          name: 'Community Vote',
          description: `
            <p class="mt-0 mb-2">
              Once the plan is finalized, the community votes to approve the project.  <span class="font-semibold">Please take note of the following:</span>
            </p>
            <ul class="list-disc list-inside mt-0 mb-2">
              <li class="font-semibold">To pass, greater than 50% of the residences in the ballot area must vote "YES".</li>
              <li class="font-semibold">Residences that don't return a ballot will be considered a "NO" vote.</li>
            </ul>
            <p class="mt-0">
              This is why it will be important for everyone that wants the plan to succeed to <span class="font-semibold underline">actually send in a ballot</span>.
              If the plan doesn't pass, the community will need to <span class="font-semibold underline">wait two years</span> from the date the voting period closed to try again to get traffic calming.
            </p>
          `, 
          selected: false,
          current: false,
          done: '06/13/2026',
        },
        {
          name: 'Board of Supervisors\' Endorsement',
          description: `
            If the plan passes, FCDOT requests endorsement of the plan from the Fairfax County Board of Supervisors.
            The Board of Supervisors resolution is sent to VDOT for review. If VDOT approves the plan,
            FCDOT sends the traffic calming plan to the Department of Public Works and Environmental Services  (DPWES)
            for installation.
          `, 
          selected: false,
          current: false,
          done: '08/25/2026',
        },
        { 
          name: 'Install Traffic Calming', 
          description: `
            The Department of Public Works and Environmental Services (DPWES) installs the traffic calming devices.
          `, 
          selected: false,
          current: true,
          done: null,
        },
      ],
    };
  },
  methods: {
    showPopover(event, index) {
      event.selected = true;
      createPopper(this.$refs.circleRefs[index], this.$refs.popoverRefs[index], {
        placement: "bottom"
      });
    },
    hidePopover(event) {
      event.selected = false;
    },
  },
};
</script>

<style scoped>
.line {
  position: absolute;
  content: "";
  width: 100%;
  height: 15px;
  background-color: #d5dfe5;
  border-radius: 15px;
  margin-top: 7.5px;
}

.event {
  content: "";
}

.circle {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #56cbf9;
  color: #1c77c3;
  border: 5px solid #1c77c3;
  transition: transform .5s ease;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.circle.selected {
  background-color: #1c77c3;
  border: 5px solid #56cbf9;
  color: #56cbf9;
}

.circle.current {
  background-color: #1c77c3;
  border: 0;
  color: gold;
}

.arrow {
  visibility: hidden;
}

.arrow::before {
  visibility: visible;
  content: '';
  transform: rotate(45deg);
}

.arrow,
.arrow::before {
  position: absolute;
  width: 16px;
  height: 16px;
  background: #075985;
}

.tooltip[data-popper-placement^='top'] > .arrow::before {
  background: rgba(186, 230, 253, var(--tw-bg-opacity));
}

.tooltip[data-popper-placement^='top'] > .arrow {
  bottom: -8px;
}

.tooltip[data-popper-placement^='bottom'] > .arrow {
  top: -8px;
}

.tooltip[data-popper-placement^='left'] > .arrow {
  right: -8px;
}

.tooltip[data-popper-placement^='right'] > .arrow {
  left: -8px;
}
</style>
